import { useState } from 'react';
import { day, money } from '../lib/format';
import { useI18n } from '../lib/i18n';

// Dû et récupéré par semaine (BA-044, spec 08 §1) : barres groupées sur un seul axe, palette
// validée (contraste et daltonisme), légende, info-bulle au survol, tableau équivalent.

// Couleurs du thème (variables CSS) : le graphique suit le mode clair ou sombre.
const COLORS = { due: 'var(--warning)', recovered: 'var(--primary)' } as const;
const WIDTH = 720;
const HEIGHT = 220;
const PAD = { top: 12, right: 8, bottom: 28, left: 8 };

export interface WeekPoint {
  weekStart: string;
  due: number;
  recovered: number;
}

export function WeeklyChart({ weeks }: { weeks: WeekPoint[] }) {
  const { t, language } = useI18n();
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...weeks.flatMap((w) => [w.due, w.recovered]));
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const band = (WIDTH - PAD.left - PAD.right) / Math.max(1, weeks.length);
  const barW = Math.max(4, Math.min(18, band / 3));
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;
  const bar = (x: number, value: number, color: string) => {
    const h = Math.max(value > 0 ? 2 : 0, (value / max) * plotH);
    const top = PAD.top + plotH - h;
    // Extrémité de données arrondie, ancrée à la ligne de base.
    const r = Math.min(4, barW / 2, h);
    return (
      <path
        d={`M${x},${top + h} V${top + r} Q${x},${top} ${x + r},${top} H${x + barW - r} Q${x + barW},${top} ${x + barW},${top + r} V${top + h} Z`}
        fill={color}
      />
    );
  };
  const active = hover === null ? null : weeks[hover];

  return (
    <figure className="m-0">
      <div className="mb-2 flex flex-wrap gap-4 text-xs text-muted-foreground" aria-hidden>
        {(['due', 'recovered'] as const).map((k) => (
          <span key={k} className="flex items-center gap-1">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ background: COLORS[k] }}
            />
            {t.dashboard[k]}
          </span>
        ))}
      </div>
      <div className="relative">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="h-auto w-full"
          role="img"
          aria-label={t.dashboard.chart}
          onMouseLeave={() => setHover(null)}
        >
          {[0.5, 1].map((f) => (
            <line
              key={f}
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={y(max * f)}
              y2={y(max * f)}
              stroke="var(--border)"
              strokeDasharray="2 4"
            />
          ))}
          <line
            x1={PAD.left}
            x2={WIDTH - PAD.right}
            y1={PAD.top + plotH}
            y2={PAD.top + plotH}
            stroke="var(--input)"
          />
          {weeks.map((w, i) => {
            const x0 = PAD.left + i * band + (band - (barW * 2 + 2)) / 2;
            return (
              <g key={w.weekStart} onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)}>
                <rect
                  x={PAD.left + i * band}
                  y={PAD.top}
                  width={band}
                  height={plotH}
                  fill={hover === i ? 'var(--muted)' : 'transparent'}
                />
                {bar(x0, w.due, COLORS.due)}
                {bar(x0 + barW + 2, w.recovered, COLORS.recovered)}
                {i % 2 === weeks.length % 2 ? (
                  <text
                    x={PAD.left + i * band + band / 2}
                    y={HEIGHT - 8}
                    textAnchor="middle"
                    fontSize="11"
                    fill="var(--muted-foreground)"
                  >
                    {day(w.weekStart, language).slice(0, 5)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
        {active ? (
          <div
            role="status"
            className="pointer-events-none absolute top-1 rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-card"
            style={{ left: `${Math.min(70, ((hover as number) / weeks.length) * 100)}%` }}
          >
            <p className="font-medium">
              {t.dashboard.weekOf} {day(active.weekStart, language)}
            </p>
            <p>
              {t.dashboard.due} : {money(active.due, language)}
            </p>
            <p>
              {t.dashboard.recovered} : {money(active.recovered, language)}
            </p>
          </div>
        ) : null}
      </div>
      <table className="sr-only">
        <caption>{t.dashboard.chart}</caption>
        <thead>
          <tr>
            <th scope="col">{t.dashboard.weekOf}</th>
            <th scope="col">{t.dashboard.due}</th>
            <th scope="col">{t.dashboard.recovered}</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => (
            <tr key={w.weekStart}>
              <td>{day(w.weekStart, language)}</td>
              <td>{money(w.due, language)}</td>
              <td>{money(w.recovered, language)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
